import React, { useRef } from "react";
import {
  Box,
  Typography,
  Modal,
  IconButton,
  Tooltip,
  Stack,
  TextField,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import InstagramIcon from "@mui/icons-material/Instagram";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CryptoJS from "crypto-js";
import SecondaryButton from "../common/SecondaryButton";

const YourProjectDetails = ({
  data,
  selectedPlot,
  showPopup,
  setShowPopup,
  selectedProperty,
  setSelectedProperty,
}) => {
  const navigate = useNavigate();
  const modalRef = useRef();
  const theme = useSelector((state) => state.themeValue.colors);
  const userId = useSelector((state) => state.user.userId);
  const encryptionKey = "yourSecretKey";

  const getShareUrl = (property) => {
    const baseUrl = window.location.origin;
    const encryptedPropertyId = CryptoJS.AES.encrypt(
      property.id.toString(),
      encryptionKey
    ).toString();
    const encryptedUserId = CryptoJS.AES.encrypt(userId.toString(), encryptionKey).toString();

    const path =
      property.type === "property"
        ? `PropertyDetailsScreen?propertyId=${encodeURIComponent(
          encryptedPropertyId
        )}&userId=${encodeURIComponent(encryptedUserId)}`
        : `ProjectDetailsScreen?projectId=${encodeURIComponent(
          encryptedPropertyId
        )}&userId=${encodeURIComponent(encryptedUserId)}`;

    return `${baseUrl}/${path}`;
  };

  return (
    <Box
      sx={{
        width: { xs: "100%", sm: "100%", md: "60%" },
        mx: "auto",
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      {/* Project Info */}
      <Box
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: theme.background,
          border: `1px solid ${theme.borderColor}`,
        }}
      >
        <Typography
          variant="h5"
          sx={{ color: theme.text, mb: 1.5, fontWeight: 600 }}
        >
          {data.project_name}
        </Typography>

        <Stack direction="row" alignItems="center" spacing={1} mb={1}>
          <LocationOnIcon sx={{ color: theme.normal_icon }} />
          <Typography sx={{ color: theme.text, fontSize: 14 }}>
            {data.address}, {data.city}
          </Typography>
        </Stack>

        <Typography sx={{ color: theme.text, fontSize: 14 }}>
          <strong>Number of Plots:</strong> {data.number_of_plots}
        </Typography>
      </Box>

      {/* Separator */}
      <Box
        sx={{
          width: "100%",
          borderBottom: "2px dotted #ccc",
          // my: 2,
        }}
      />

      {/* Plot Info */}
      {selectedPlot ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="h6" sx={{ color: theme.text }}>
            {selectedPlot.plotName}
          </Typography>

          <Stack direction="column" sx={{
            gap: { xs: 1, sm: 3 } // responsive spacing
          }} flexWrap="wrap">
            <Typography sx={{ color: theme.text }}>
              <strong>Facing:</strong> {selectedPlot.Facing}
            </Typography>
            <Typography sx={{ color: theme.text }}>
              <strong>Size:</strong> {selectedPlot.SizeInSqFt} sqft
            </Typography>
            <Typography sx={{ color: theme.text }}>
              <strong>Price:</strong> ₹{selectedPlot.SqFtPrice}/sqft
            </Typography>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            flexWrap="wrap"
            justifyContent="flex-start"
            alignItems="flex-start"
          >
            <SecondaryButton
              text="Edit Plot"
              width={150}
              height={50}
              onClick={() =>
                navigate("/editproperty", {
                  state: {
                    propertyData: [
                      {
                        id: selectedPlot.id,
                        PropertyName: selectedPlot.plotName,
                        City: data.city,
                        Address: data.address,
                        SizeInSqFt: selectedPlot.SizeInSqFt,
                        SqFtPrice: selectedPlot.SqFtPrice,
                      },
                    ],
                  },
                })
              }
            />

            <SecondaryButton
              text="MarketUpdate"
              width={150}
              height={50}
              onClick={() =>
                navigate("/priceupdate", {
                  state: {
                    propertyData: {
                      PropertyId: selectedPlot.id,
                      PropertyName: selectedPlot.plotName,
                      City: data.city,
                      Address: data.address,
                      SqFtPrice: selectedPlot.SqFtPrice,
                      MarketPrice: selectedPlot.SqFtPrice * selectedPlot.SizeInSqFt,
                    },
                  },
                })
              }
            />
          </Stack>


          {/* Share Popup */}
          {showPopup && selectedProperty && (
            <Modal
              open={showPopup}
              onClose={() => setShowPopup(false)}
              aria-labelledby="share-property-modal"
              sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
            >
              <Box
                ref={modalRef}
                sx={{
                  bgcolor: theme.background,
                  p: 3,
                  borderRadius: 2,
                  width: { xs: "90%", sm: "400px" },
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <Typography variant="h6">Share Property</Typography>

                <Stack direction="row" spacing={1}>
                  <TextField
                    value={getShareUrl(selectedProperty)}
                    size="small"
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                  <Tooltip title="Copy Link">
                    <IconButton
                      onClick={() => {
                        navigator.clipboard.writeText(getShareUrl(selectedProperty));
                        alert("Link copied to clipboard!");
                      }}
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>

                <Stack direction="row" spacing={2}>
                  <Tooltip title="Share on WhatsApp">
                    <IconButton
                      sx={{ bgcolor: "#25D366", color: "#fff" }}
                      onClick={() => {
                        const message = encodeURIComponent(
                          `Check this property: ${getShareUrl(selectedProperty)}`
                        );
                        window.open(`https://wa.me/?text=${message}`, "_blank");
                      }}
                    >
                      <WhatsAppIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Share on Instagram">
                    <IconButton
                      sx={{ bgcolor: "#E1306C", color: "#fff" }}
                      onClick={() => {
                        navigator.clipboard.writeText(getShareUrl(selectedProperty));
                        alert("Link copied! Paste it into Instagram.");
                      }}
                    >
                      <InstagramIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Box>
            </Modal>
          )}
        </Box>
      ) : (
        <Typography sx={{ color: theme.text }}>Select a plot to view details</Typography>
      )}
    </Box>
  );
};

export default YourProjectDetails;
